import ErrorTemplate from '@/components/layouts/ErrorTemplate';
import LayoutError from '@/components/layouts/LayoutError';
import AfterDoc from '@/components/ui/AfterDoc';
import { PRODUCT } from '@/consts';
import { CONSULTANTS } from '@/features/v2/consultants';
import ProgressFinished from '@/features/v2/vehicle-damage/components/thankYou/ProgressFinished';
import cn from '@/utils/cn';

import { useLingui } from '@lingui/react';

export enum SomethingWentWrongVariants {
  Refresh = 'refresh',
  Comeback = 'comeback',
}

const SomethingWentWrong = ({
  code,
  message,
  variant = SomethingWentWrongVariants.Refresh,
  productId,
}: {
  code?: string | number;
  message?: string;
  variant?: SomethingWentWrongVariants;
  productId?: number;
}) => {
  const { i18n } = useLingui();

  if (import.meta.env.VITE_SHOW_LOGS === 'true') {
    return (
      <LayoutError>
        <ErrorTemplate
          code={code ?? 500}
          message={
            message ??
            i18n.t(
              'Wygląda na to, że to nasza wina, zrób zrzut ekranu z otwartą konsolą (console + network) i zgłoś nam błąd'
            )
          }
        />
      </LayoutError>
    );
  }

  return (
    <LayoutError>
      <div className="flex flex-col-reverse gap-5 lg:flex-row">
        <AfterDoc
          consultant={
            productId === PRODUCT.PIT ? undefined : CONSULTANTS.MARTA_ASSISTANT
          }
          title={i18n.t('Napotkaliśmy problem')}
          thankYouMessage={i18n.t(
            'Wystąpił błąd, który uniemożliwia kontynuację działania'
          )}
          withButton
          icon={'warning'}
          productId={productId}
        >
          {variant === SomethingWentWrongVariants.Refresh && (
            <>
              <p>
                {i18n.t(
                  'Prosimy o odświeżenie strony. Jeśli to nie pomoże, skontaktuj się z nami –  pomożemy rozwiązać sytuację. Dziękujemy za zrozumienie.'
                )}
              </p>
              <p>{i18n.t('W razie pilnej potrzeby, zadzwoń do nas.')}</p>
            </>
          )}
          {variant === SomethingWentWrongVariants.Comeback && (
            <>
              <p>
                {i18n.t(
                  'Prosimy o sprawdzenie skrzynki e-mail i ponowne wejście do panelu, poprzez link otrzymany w wiadomości. Jeśli to nie rozwiąże problemu, prosimy o kontakt – chętnie pomożemy. Dziękujemy za wyrozumiałość.'
                )}
              </p>
              <p>
                {i18n.t('Kod błędu:')} {code}
              </p>
              <p>{i18n.t('W razie pilnej potrzeby, zadzwoń do nas.')}</p>
            </>
          )}
        </AfterDoc>
        <div
          className={cn(
            'fixed inset-x-1/2 top-[75px] z-40 w-full -translate-x-1/2',
            'lg:relative lg:inset-x-0 lg:top-0 lg:max-w-[290px] lg:-translate-x-0'
          )}
        >
          <div className={cn('relative w-full')}>
            <span className="translate-z-0 absolute left-0 top-[-12px] flex h-[41px] w-screen bg-oxford-blue-400 lg:hidden" />
            <div className="mx-auto px-2.5 sm:max-w-screen-sm sm:px-4 md:max-w-tablet md:px-6 lg:max-w-none lg:px-0">
              <ProgressFinished
                steps={[
                  {
                    label:
                      variant === SomethingWentWrongVariants.Refresh
                        ? 'Odśwież stronę'
                        : 'Wróć przez link w mailu',
                    checked: false,
                    unChecked: false,
                    active: true,
                  },
                  {
                    label: 'Kontynuuj proces',
                    checked: false,
                    active: false,
                  },
                ]}
                variant={'ups'}
              />
            </div>
          </div>
        </div>
      </div>
    </LayoutError>
  );
};

export default SomethingWentWrong;
